import { useState, useEffect } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', lavender: '#A78BFA', text: '#E8EDF5', sub: '#C5CDD8',
};

const ROLE_BADGE = {
  owner:   { label: 'Owner',   bg: 'rgba(167,139,250,0.15)', color: '#A78BFA', border: 'rgba(167,139,250,0.3)' },
  admin:   { label: 'Admin',   bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8', border: 'rgba(56,189,248,0.3)'  },
  manager: { label: 'Manager', bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)'  },
  cashier: { label: 'Cashier', bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'   },
  staff:   { label: 'Staff',   bg: 'rgba(136,146,164,0.1)',  color: '#8892A4', border: 'rgba(136,146,164,0.2)' },
};

const STATUS_BADGE = {
  active:  { label: 'Active',  color: '#00C9B1', bg: 'rgba(0,201,177,0.1)',  border: 'rgba(0,201,177,0.25)'  },
  pending: { label: 'Pending', color: '#F0B429', bg: 'rgba(240,180,41,0.1)', border: 'rgba(240,180,41,0.25)' },
};

const STYLES = `
  .tm-page { font-family: 'Cabinet Grotesk', sans-serif; max-width: 1200px; margin: 0 auto; padding: 32px; }
  .tm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 28px; flex-wrap: wrap; }
  .tm-title { font-size: 1.55rem; font-weight: 800; color: ${C.text}; margin: 0; letter-spacing: -0.3px; }
  .tm-ws-name { font-size: 0.82rem; color: ${C.muted}; margin: 4px 0 0; }
  .tm-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .tm-btn-primary { background: ${C.gold}; color: ${C.ink}; border: none; border-radius: 10px; padding: 10px 18px; font-size: 0.84rem; font-weight: 700; cursor: pointer; transition: opacity 150ms; font-family: inherit; white-space: nowrap; }
  .tm-btn-primary:hover { opacity: 0.88; }
  .tm-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .tm-btn-outline { background: rgba(255,255,255,0.05); color: ${C.sub}; border: 1px solid ${C.border}; border-radius: 10px; padding: 10px 18px; font-size: 0.84rem; font-weight: 600; cursor: pointer; transition: all 150ms; font-family: inherit; white-space: nowrap; }
  .tm-btn-outline:hover { background: rgba(255,255,255,0.09); color: ${C.text}; border-color: ${C.muted}; }
  .tm-ws-tabs { display: flex; gap: 6px; margin-bottom: 20px; flex-wrap: wrap; }
  .tm-ws-tab { padding: 7px 16px; border-radius: 8px; font-size: 0.8rem; font-weight: 600; cursor: pointer; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.muted}; font-family: inherit; transition: all 150ms; }
  .tm-ws-tab.active { background: ${C.gold}; color: ${C.ink}; border-color: ${C.gold}; }
  .tm-ws-tab:hover:not(.active) { color: ${C.text}; background: rgba(255,255,255,0.07); }
  .tm-count { font-size: 0.72rem; opacity: 0.75; margin-left: 5px; }
  .tm-wrap { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px; overflow: hidden; }
  .tm-table { width: 100%; border-collapse: collapse; }
  .tm-table thead th { padding: 12px 16px; font-size: 0.7rem; font-weight: 700; color: ${C.muted}; letter-spacing: 0.06em; text-transform: uppercase; text-align: left; border-bottom: 1px solid ${C.border}; background: rgba(255,255,255,0.02); white-space: nowrap; }
  .tm-table tbody tr { border-bottom: 1px solid ${C.border}; transition: background 150ms; }
  .tm-table tbody tr:last-child { border-bottom: none; }
  .tm-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .tm-td { padding: 14px 16px; font-size: 0.875rem; color: ${C.text}; vertical-align: middle; }
  .tm-select { padding: 5px 8px; background: rgba(255,255,255,0.05); border: 1px solid ${C.border}; border-radius: 7px; font-size: 0.78rem; color: ${C.sub}; font-family: inherit; outline: none; cursor: pointer; }
  .tm-select:focus { border-color: ${C.teal}; }
  .tm-btn-sm-danger { padding: 5px 12px; border-radius: 8px; font-size: 0.75rem; font-weight: 600; cursor: pointer; border: 1px solid rgba(255,107,107,0.35); background: rgba(255,107,107,0.07); color: ${C.coral}; font-family: inherit; transition: all 140ms; }
  .tm-btn-sm-danger:hover { background: rgba(255,107,107,0.14); }
  .tm-empty { padding: 64px 20px; text-align: center; }
  .tm-loading { padding: 44px; text-align: center; color: ${C.muted}; font-size: 0.875rem; }
  .tm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .tm-modal { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 18px; width: 100%; max-width: 460px; padding: 28px; box-shadow: 0 24px 60px rgba(0,0,0,0.55); }
  .tm-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
  .tm-modal-title { font-size: 1.1rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .tm-modal-close { background: none; border: none; color: ${C.muted}; font-size: 1.25rem; cursor: pointer; padding: 0; line-height: 1; }
  .tm-modal-close:hover { color: ${C.text}; }
  .tm-field { margin-bottom: 18px; }
  .tm-label { display: block; font-size: 0.72rem; font-weight: 700; color: ${C.muted}; margin-bottom: 6px; letter-spacing: 0.04em; text-transform: uppercase; }
  .tm-input { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color 150ms; }
  .tm-input:focus { border-color: ${C.teal}; }
  .tm-input::placeholder { color: ${C.muted}; }
  .tm-select-full { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color 150ms; }
  .tm-select-full:focus { border-color: ${C.teal}; }
  .tm-modal-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; border-top: 1px solid ${C.border}; padding-top: 20px; }
  .tm-btn-cancel { padding: 10px 18px; border-radius: 10px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.muted}; font-weight: 600; font-size: 0.85rem; cursor: pointer; font-family: inherit; }
  .tm-btn-cancel:hover { background: rgba(255,255,255,0.07); color: ${C.text}; }
  .tm-upgrade-banner { background: rgba(240,180,41,0.1); border: 1px solid rgba(240,180,41,0.25); border-radius: 10px; padding: 10px 14px; font-size: 0.82rem; color: ${C.gold}; margin-bottom: 20px; }
`;

export default function Team() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [workspaces, setWorkspaces]           = useState([]);
  const [activeWs, setActiveWs]               = useState(null);
  const [members, setMembers]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteEmail, setInviteEmail]         = useState('');
  const [inviteRole, setInviteRole]           = useState('staff');
  const [wsName, setWsName]                   = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [toast, setToast]                     = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const isPro     = ['trial', 'basic', 'growth', 'enterprise', 'pro', 'business'].includes(user?.plan);
  const myRole    = activeWs?.myRole;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const loadWorkspaces = async () => {
    try {
      const { data } = await api.get('/workspaces');
      setWorkspaces(data);
      if (data.length > 0) {
        const current = data.find((w) => w.isCurrent) || data[0];
        setActiveWs(current);
        loadMembers(current.id);
      }
    } catch {
      showToast('Failed to load workspaces', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceSwitch = async (workspace) => {
    setActiveWs(workspace);
    await loadMembers(workspace.id);
    try {
      const { data } = await api.put('/workspaces/current', { workspaceId: workspace.id });
      if (data.user) updateUser(data.user);
      setWorkspaces((cur) =>
        cur.map((entry) => ({ ...entry, isCurrent: entry.id === workspace.id }))
      );
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to switch workspace', 'error');
    }
  };

  const loadMembers = async (wsId) => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/members`);
      setMembers(data);
    } catch {
      showToast('Failed to load members', 'error');
    }
  };

  useEffect(() => { loadWorkspaces(); }, []);

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/workspaces', { name: wsName });
      showToast(`Workspace "${data.name}" created`);
      setShowCreateModal(false);
      setWsName('');
      loadWorkspaces();
    } catch (err) {
      const e = err.response?.data;
      if (e?.upgrade) {
        showToast('Upgrade to Pro to create workspaces', 'error');
        setShowCreateModal(false);
        navigate('/pricing');
      } else {
        showToast(e?.error || 'Failed to create workspace', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/workspaces/${activeWs.id}/invite`, { email: inviteEmail, role: inviteRole });
      showToast(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('staff');
      loadMembers(activeWs.id);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send invitation', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await api.put(`/workspaces/${activeWs.id}/members/${member.id}`, { role: newRole });
      loadMembers(activeWs.id);
      showToast(`${member.user?.name || member.inviteEmail}'s role updated to ${newRole}`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update role', 'error');
    }
  };

  const handleRemove = async (member) => {
    const name = member.user?.name || member.inviteEmail;
    if (!confirm(`Remove ${name} from this workspace?`)) return;
    try {
      await api.delete(`/workspaces/${activeWs.id}/members/${member.id}`);
      loadMembers(activeWs.id);
      showToast(`${name} removed from workspace`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to remove member', 'error');
    }
  };

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="tm-page">
        <div className="tm-header">
          <div>
            <h1 className="tm-title">Team</h1>
            {activeWs && <p className="tm-ws-name">{activeWs.name}</p>}
          </div>
          <div className="tm-header-actions">
            <button className="tm-btn-outline" onClick={() => setShowCreateModal(true)}>
              + New Workspace
            </button>
            {activeWs && (
              isPro ? (
                <button
                  className="tm-btn-primary"
                  onClick={() => setShowInviteModal(true)}
                  disabled={!canManage}
                  title={canManage ? '' : 'Only admins and owners can invite members'}
                >
                  + Invite Member
                </button>
              ) : (
                <button
                  className="tm-btn-primary"
                  onClick={() => navigate('/pricing')}
                  title="Upgrade to Pro to invite team members"
                >
                  Upgrade to Invite
                </button>
              )
            )}
          </div>
        </div>

        {loading ? (
          <div className="tm-loading">Loading...</div>
        ) : workspaces.length === 0 ? (
          <div className="tm-wrap">
            <div className="tm-empty">
              <p style={{ color: C.muted, marginBottom: 16 }}>You don&apos;t have any workspaces yet.</p>
              {isPro ? (
                <button className="tm-btn-primary" onClick={() => setShowCreateModal(true)}>
                  Create your first workspace
                </button>
              ) : (
                <button className="tm-btn-primary" onClick={() => navigate('/pricing')}>
                  Upgrade to Pro to create workspaces
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {workspaces.length > 1 && (
              <div className="tm-ws-tabs">
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => handleWorkspaceSwitch(ws)}
                    className={`tm-ws-tab${activeWs?.id === ws.id ? ' active' : ''}`}
                  >
                    {ws.name}
                    <span className="tm-count">{ws.memberCount}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="tm-wrap">
              <table className="tm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const rb = ROLE_BADGE[m.role]     || ROLE_BADGE.staff;
                    const sb = STATUS_BADGE[m.status] || { label: m.status, color: C.muted, bg: 'rgba(136,146,164,0.08)', border: 'rgba(136,146,164,0.2)' };
                    const isOwner = m.role === 'owner';
                    return (
                      <tr key={m.id}>
                        <td className="tm-td" style={{ fontWeight: 600 }}>
                          {m.user?.name || <span style={{ color: C.muted }}>Pending</span>}
                        </td>
                        <td className="tm-td" style={{ color: C.muted }}>{m.user?.email || m.inviteEmail}</td>
                        <td className="tm-td">
                          <span style={{
                            display: 'inline-block', fontSize: '0.7rem', fontWeight: 700,
                            padding: '3px 10px', borderRadius: 20,
                            background: rb.bg, color: rb.color, border: `1px solid ${rb.border}`,
                          }}>
                            {rb.label}
                          </span>
                        </td>
                        <td className="tm-td">
                          <span style={{
                            display: 'inline-block', fontSize: '0.7rem', fontWeight: 700,
                            padding: '3px 10px', borderRadius: 20,
                            background: sb.bg, color: sb.color, border: `1px solid ${sb.border}`,
                          }}>
                            {sb.label}
                          </span>
                        </td>
                        <td className="tm-td" style={{ color: C.muted, fontSize: '0.82rem' }}>
                          {new Date(m.joinedAt).toLocaleDateString()}
                        </td>
                        <td className="tm-td">
                          {!isOwner && canManage && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <select
                                className="tm-select"
                                value={m.role}
                                onChange={(e) => handleRoleChange(m, e.target.value)}
                              >
                                <option value="admin">Admin</option>
                                <option value="manager">Manager</option>
                                <option value="cashier">Cashier</option>
                                <option value="staff">Staff</option>
                              </select>
                              <button
                                className="tm-btn-sm-danger"
                                onClick={() => handleRemove(m)}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="tm-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="tm-modal">
            <div className="tm-modal-header">
              <h3 className="tm-modal-title">New Workspace</h3>
              <button className="tm-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div style={{
              padding: '14px 16px',
              background: 'rgba(240,180,41,0.08)',
              border: '1px solid rgba(240,180,41,0.25)',
              borderRadius: 10,
              marginBottom: 20,
              fontSize: '0.8rem',
              color: C.gold,
              lineHeight: 1.6,
            }}>
              <strong>Before you create a new workspace:</strong> a workspace is a completely
              separate business with its own products, sales and data.
              {activeWs ? (
                <> To add a staff member to <strong>{activeWs.name}</strong> instead, close this
                and use <strong>+ Invite Member</strong> above.</>
              ) : (
                <> To add a staff member to an existing business instead, use <strong>Invite Member</strong>.</>
              )}
            </div>
            <form onSubmit={handleCreateWorkspace}>
              <div className="tm-field">
                <label className="tm-label">Workspace Name</label>
                <input
                  className="tm-input"
                  placeholder="e.g. Sunrise General Store"
                  value={wsName}
                  onChange={(e) => setWsName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="tm-modal-footer">
                <button type="button" className="tm-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="tm-btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Workspace'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="tm-overlay" onClick={(e) => e.target === e.currentTarget && setShowInviteModal(false)}>
          <div className="tm-modal">
            <div className="tm-modal-header">
              <h3 className="tm-modal-title">
                {activeWs ? `Invite someone to join ${activeWs.name}` : 'Invite Member'}
              </h3>
              <button className="tm-modal-close" onClick={() => setShowInviteModal(false)}>✕</button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="tm-field">
                <label className="tm-label">Email Address</label>
                <input
                  className="tm-input"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="tm-field">
                <label className="tm-label">Role</label>
                <select className="tm-select-full" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <option value="admin">Admin — can manage members and all data</option>
                  <option value="manager">Manager — can manage inventory and sales</option>
                  <option value="cashier">Cashier — POS and sales only</option>
                  <option value="staff">Staff — view and record sales only</option>
                </select>
              </div>
              <div className="tm-modal-footer">
                <button type="button" className="tm-btn-cancel" onClick={() => setShowInviteModal(false)}>Cancel</button>
                <button type="submit" className="tm-btn-primary" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </Layout>
  );
}
